import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#003264] text-white" data-testid="footer">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/logo-iks.png" 
                alt="IKS Propreté & Services" 
                className="h-20 w-auto object-contain"
              />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Service de nettoyage professionnel dans l'Hérault. 
              Particuliers et professionnels, nous prenons soin de vos espaces.
            </p>
            <div className="flex items-center gap-2 text-sm text-emerald-400 font-medium">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              Disponible 7j/7
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              {[
                { label: "Ménage régulier", href: "/particuliers" },
                { label: "Nettoyage vitres", href: "/particuliers" },
                { label: "Remise en état", href: "/particuliers" },
                { label: "Nettoyage pro", href: "/professionnels" },
              ].map((item, i) => (
                <li key={i}>
                  <Link 
                    to={item.href} 
                    className="text-white/60 hover:text-white text-sm transition-colors flex items-center gap-1 group"
                  >
                    {item.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:0625168027" 
                  className="flex items-center gap-3 text-white/60 hover:text-white text-sm transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-white">06 25 16 80 27</p>
                    <p className="text-xs text-white/40">Appelez-nous</p>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:contact@iks-services.fr" 
                  className="flex items-center gap-3 text-white/60 hover:text-white text-sm transition-colors group"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-white">contact@iks-services.fr</p>
                    <p className="text-xs text-white/40">Écrivez-nous</p>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3 text-white/60">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Hérault (34)</p>
                    <p className="text-xs text-white/40">Zone d'intervention</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* Info */}
          <div>
            <h4 className="font-bold text-lg mb-6">Informations</h4>
            <ul className="space-y-3">
              {[
                { label: "Accueil", href: "/" },
                { label: "Particuliers", href: "/particuliers" },
                { label: "Professionnels", href: "/professionnels" },
                { label: "Contact", href: "/contact" },
              ].map((item, i) => (
                <li key={i}>
                  <Link 
                    to={item.href} 
                    className="text-white/60 hover:text-white text-sm transition-colors flex items-center gap-1 group"
                  >
                    {item.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Tax credit badge */}
            <div className="mt-6 p-4 bg-white/10 rounded-xl">
              <p className="text-sm font-medium text-white mb-1">Avance immédiate</p>
              <p className="text-2xl font-bold text-emerald-400">-50%</p>
              <p className="text-xs text-white/50 mt-1">Crédit d'impôt pour les particuliers</p>
            </div>
          </div>
        </div>
        
        {/* SEO: Cities served */}
        <div className="border-t border-white/10 pt-6 pb-2 max-w-7xl mx-auto px-6">
          <p className="text-xs text-white/30 text-center leading-relaxed">
            Société de nettoyage intervenant dans tout l'Hérault (34) : <strong className="text-white/40">Montpellier</strong>, <strong className="text-white/40">Béziers</strong>, <strong className="text-white/40">Sète</strong>, Agde, Lunel, Castelnau-le-Lez, Lattes, Mauguio, Pérols, Saint-Jean-de-Védas, Frontignan, Palavas-les-Flots, La Grande-Motte, Grabels, Juvignac, Saint-Gély-du-Fesc, Clapiers, Jacou, Vendargues, Fabrègues, Pignan, Cournonterral, Villeneuve-lès-Maguelone, Lodève, Clermont-l'Hérault, Pézenas et toutes les communes du département.
          </p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-white/40">
              © {currentYear} IKS - Propreté & Services. Tous droits réservés.
            </p>
            <div className="flex items-center gap-6 text-sm text-white/40">
              <span>SIRET: XXX XXX XXX XXXXX</span>
              <span className="hidden md:block">•</span>
              <span>Service à la personne agréé</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
